import { useEffect, useMemo } from 'react';
import { useQuery, skipToken } from '@apollo/client/react';

import { usePagination } from 'common/hooks/usePagination';
import {
    GroupUsersDocument,
    SkillCertificateMatrixReportDocument,
} from 'graphql/types';

interface IUseSkillCertificateMatrixOptions {
    groupId: string;
    skillId?: string;
    searchQuery?: string;
    skip?: boolean;
    isCompliant?: boolean;
    progressFrom?: number;
    progressTo?: number;
}

export const useSkillCertificateMatrix = ({
    groupId,
    skillId,
    searchQuery,
    skip,
    isCompliant,
    progressFrom,
    progressTo,
}: IUseSkillCertificateMatrixOptions) => {
    const hasActiveFilters =
        isCompliant != null || progressFrom != null || progressTo != null;

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const reportQueryVariables = useMemo(
        () => ({
            groupId,
            skillIds: [skillId!],
            isCompliant,
            progressFrom,
            progressTo,
        }),
        [groupId, skillId, isCompliant, progressFrom, progressTo]
    );

    // Fetch portfolio report using groupId — backend resolves users from group
    const { data: reportData, loading: reportLoading } = useQuery(
        SkillCertificateMatrixReportDocument,
        !skip && !!groupId && !!skillId
            ? { variables: reportQueryVariables }
            : skipToken
    );

    const reportItems = reportData?.skillPortfolioReport || [];

    // Extract unique user IDs from report results to filter the users query
    const reportUserIds = useMemo(
        () => [
            ...new Set(
                reportItems.map((item) => item.user?.id).filter(Boolean)
            ),
        ],
        [reportItems]
    );

    // When filters are active, pass report user IDs to only fetch matching users.
    // Only include `ids` when filtering to keep the cache key consistent.
    const userQueryVariables = useMemo(
        () => ({
            id: groupId,
            ...(hasActiveFilters && { ids: reportUserIds }),
            q: searchQuery || undefined,
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [
            groupId,
            hasActiveFilters,
            reportUserIds,
            searchQuery,
            paginationSettings.offset,
            paginationSettings.first,
        ]
    );

    const { data: groupUsersData, loading: usersLoading } = useQuery(
        GroupUsersDocument,
        !skip && !!groupId && !(hasActiveFilters && reportLoading)
            ? { variables: userQueryVariables }
            : skipToken
    );

    const usersCount = groupUsersData?.group?.users?.count;

    const users = useMemo(
        () =>
            groupUsersData?.group?.users?.edges
                ?.map((e) => e?.node)
                .filter(Boolean) || [],
        [groupUsersData]
    );

    useEffect(() => {
        if (usersLoading || !initializePagination) return;

        initializePagination(usersCount ?? 0);
    }, [usersCount, usersLoading, initializePagination]);

    const loading = usersLoading || reportLoading;

    return {
        users,
        reportItems,
        loading,
        paginationSettings,
        setPage,
    };
};
